/**
 * Tooltip - lightweight hover tooltip with configurable delay and placement.
 *
 * Shows after 200ms (configurable) on hover.
 * Uses a portal to escape overflow:hidden clipping in graph nodes.
 */

import { useState, useRef, useCallback, useEffect, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

type Placement = 'top' | 'right';

interface TooltipProps {
  text: string | undefined;
  delay?: number;
  placement?: Placement;
  children: ReactNode;
}

const baseStyle: CSSProperties = {
  position: 'fixed',
  padding: '4px 8px',
  background: '#1f2937',
  color: '#d1d5db',
  fontSize: 11,
  lineHeight: '1.4',
  borderRadius: 4,
  border: '1px solid #4b5563',
  whiteSpace: 'normal',
  width: 'max-content',
  maxWidth: 200,
  pointerEvents: 'none',
  zIndex: 9999,
  boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
};

function getPosition(rect: DOMRect, placement: Placement) {
  if (placement === 'right') {
    return {
      style: {
        left: rect.right + 8,
        top: rect.top + rect.height / 2,
        transform: 'translateY(-50%)',
      },
    };
  }
  // default: top
  return {
    style: {
      left: rect.left + rect.width / 2,
      top: rect.top - 4,
      transform: 'translate(-50%, -100%)',
    },
  };
}

export function Tooltip({ text, delay = 200, placement = 'top', children }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [posStyle, setPosStyle] = useState<CSSProperties>({});

  const show = useCallback(() => {
    if (!text) return;
    timerRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosStyle(getPosition(rect, placement).style);
      }
      setVisible(true);
    }, delay);
  }, [text, delay, placement]);

  const hide = useCallback(() => {
    clearTimeout(timerRef.current);
    setVisible(false);
  }, []);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  if (!text) return <>{children}</>;

  return (
    <span
      ref={triggerRef}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {visible && createPortal(
        <span style={{ ...baseStyle, ...posStyle }}>
          {text}
        </span>,
        document.body
      )}
    </span>
  );
}
